import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Page } from '../models/page-wrapper.model';
import { WorkflowStatusMaster } from './workflow-status.model';
import { WorkflowTransitionCondition } from './workflow-transition-condition.model';
import { WorkflowTransitionConfig } from './workflow-transition.model';



@Injectable({
  providedIn: 'root'
})
export class WorkflowConfigService {

  private apiUrl = `${environment.restApiUrl}workflow`;

  constructor(private http: HttpClient) { }

  // --- Workflow Status Master Methods ---

  getAllStatuses(params: HttpParams): Observable<Page<WorkflowStatusMaster>> {
    return this.http.get<Page<WorkflowStatusMaster>>(`${this.apiUrl}/statuses`, { params });
  }

  getStatusById(id: number): Observable<WorkflowStatusMaster> {
    return this.http.get<WorkflowStatusMaster>(`${this.apiUrl}/statuses/${id}`);
  }

  createStatus(dto: WorkflowStatusMaster): Observable<WorkflowStatusMaster> {
    return this.http.post<WorkflowStatusMaster>(`${this.apiUrl}/statuses`, dto);
  }

  updateStatus(id: number, dto: WorkflowStatusMaster): Observable<WorkflowStatusMaster> {
    return this.http.put<WorkflowStatusMaster>(`${this.apiUrl}/statuses/${id}`, dto);
  }

  deleteStatus(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/statuses/${id}`);
  }

  // --- Workflow Transition Config Methods ---

  getAllTransitions(params: HttpParams): Observable<Page<WorkflowTransitionConfig>> {
    return this.http.get<Page<WorkflowTransitionConfig>>(`${this.apiUrl}/transitions`, { params });
  }

  getTransitionById(id: number): Observable<WorkflowTransitionConfig> {
    return this.http.get<WorkflowTransitionConfig>(`${this.apiUrl}/transitions/${id}`);
  }

  createTransition(dto: WorkflowTransitionConfig): Observable<WorkflowTransitionConfig> {
    return this.http.post<WorkflowTransitionConfig>(`${this.apiUrl}/transitions`, dto);
  }

  updateTransition(id: number, dto: WorkflowTransitionConfig): Observable<WorkflowTransitionConfig> {
    return this.http.put<WorkflowTransitionConfig>(`${this.apiUrl}/transitions/${id}`, dto);
  }

  deleteTransition(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/transitions/${id}`);
  }

  // --- Workflow Transition Condition Methods ---

  /**
   * Get all conditions for a specific transition.
   * @param transitionId The ID of the parent transition.
   * @param params Query parameters for filtering and pagination.
   */
  getConditionsByTransitionId(transitionId: number, params: HttpParams): Observable<Page<WorkflowTransitionCondition>> {
    return this.http.get<Page<WorkflowTransitionCondition>>(`${this.apiUrl}/transitions/${transitionId}/conditions`, { params });
  }

  /**
   * Get a single condition by its ID, ensuring it belongs to the correct transition.
   * @param transitionId The ID of the parent transition.
   * @param conditionId The ID of the condition.
   */
  getConditionById(transitionId: number, conditionId: number): Observable<WorkflowTransitionCondition> {
    return this.http.get<WorkflowTransitionCondition>(`${this.apiUrl}/transitions/${transitionId}/conditions/${conditionId}`);
  }

  /**
   * Create a new condition for a transition.
   * @param transitionId The ID of the parent transition.
   * @param dto The condition data to create.
   */
  createCondition(transitionId: number, dto: WorkflowTransitionCondition): Observable<WorkflowTransitionCondition> {
    return this.http.post<WorkflowTransitionCondition>(`${this.apiUrl}/transitions/${transitionId}/conditions`, dto);
  }

  /**
   * Update an existing condition.
   * @param transitionId The ID of the parent transition.
   * @param conditionId The ID of the condition to update.
   * @param dto The updated condition data.
   */
  updateCondition(transitionId: number, conditionId: number, dto: WorkflowTransitionCondition): Observable<WorkflowTransitionCondition> {
    return this.http.put<WorkflowTransitionCondition>(`${this.apiUrl}/transitions/${transitionId}/conditions/${conditionId}`, dto);
  }

  /**
   * Delete a condition.
   * @param transitionId The ID of the parent transition.
   * @param conditionId The ID of the condition to delete.
   */
  deleteCondition(transitionId: number, conditionId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/transitions/${transitionId}/conditions/${conditionId}`);
  }
}

